import React, { FC, useEffect, useState, useCallback } from "react";
import { Button, Spin, message, Steps, Modal, Typography } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Checkbox } from "antd";
import styles from "./styles.module.less";
import Axios from "axios";
import { VerifyCodeInput } from "../../../components/VerifyCodeInput";
import { getImgSrc } from "../../../utils";
import { useTranslation } from "react-i18next";
const { Paragraph } = Typography;

const { Step } = Steps;

const ScanQrcode: FC<{ qrcode: string }> = ({ qrcode }) => {
  const { t } = useTranslation();

  return (
    <div>
      <h4 className={styles.subtitle}>{t("user.scanQrcode")}</h4>
      <p className={styles.desc}>请在手机打开 Google Authenticator / Microsoft Authenticator 扫码添加 MFA</p>

      <img className={styles.qrcode} src={qrcode} alt="qrcode" />
    </div>
  );
};

export const InputSaftyCode: FC<{ saftyCode: string[]; setSaftyCode: (code: string[]) => void }> = ({
  saftyCode,
  setSaftyCode,
}) => {
  const { t } = useTranslation();

  return (
    <div>
      <h4 className={styles.subtitle}>{t("user.inputSafteyCode")}</h4>
      <p className={styles.desc}>{t("user.viewInputSixCode")}</p>

      <VerifyCodeInput className={styles.saftyCodeInput} verifyCode={saftyCode} setVerifyCode={setSaftyCode} />

      <p className={styles.saftyCodeTip}>{t("user.numberSafteyCode")}</p>
    </div>
  );
};

const SaveSecretKey: FC<{ setIsSaved: Function; isSaved: boolean; secret: string }> = ({
  secret,
  isSaved,
  setIsSaved,
}) => {
  const { t } = useTranslation();
  const handleChange = (val: boolean) => {
    setIsSaved(val);
  };
  return (
    <div>
      <h4 className={styles.subtitle}>{t("user.saveSecret")}</h4>
      <p className={styles.desc}>{t("user.resetCodeDoWhat")}</p>

      <Paragraph className={styles.secretParagraph} copyable>
        {secret}
      </Paragraph>

      <Checkbox onChange={(evt) => handleChange(evt.target.checked)} checked={isSaved}>
        {t("login.rememberedSecret")}
      </Checkbox>
    </div>
  );
};

const BindSuccess: FC = () => {
  const { t } = useTranslation();

  return (
    <div>
      <img
        width="148"
        height="148"
        style={{
          marginBottom: "34px",
        }}
        src={getImgSrc("/mfa-bind-success.png")}
        alt="bind success"
      />
      <h4 className={styles.subtitle}>{t("user.bindSuccess")}</h4>
      <p className={styles.desc}>{t("user.bindedMfa")}</p>
    </div>
  );
};

export const UserMfa: FC = () => {
  const getInitSaftyCode = () => new Array(6).fill("");
  const [saftyCode, setSaftyCode] = useState<string[]>(getInitSaftyCode());
  const [isSaved, setIsSaved] = useState(false);
  const [secret, setSecret] = useState("");
  const [qrcode, setQrcode] = useState("");
  const [btnLoading, setBtnLoading] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);

  const { t } = useTranslation();

  const handleBind = async () => {
    setBtnLoading(true);
    try {
      const data: any = await Axios.post("/api/v2/mfa/totp/associate/confirm", {
        authenticator_type: "totp",
        totp: saftyCode.join(""),
      });

      if (data.code !== 200) {
        message.error(data.message);
      } else {
        message.success(t("user.bindSuccess"));
        setCurrentStep((currentStep + 1) as keyof typeof STEP_MAP);
        setSaftyCode(getInitSaftyCode());
      }
      setBtnLoading(false);
    } catch (e) {
      message.error(t("user.bindFail"));
      setBtnLoading(false);
    }
  };

  const handleUnbind = async () => {
    console.log(
      Modal.confirm({
        title: t("user.unbindMfa"),
        icon: <ExclamationCircleOutlined />,
        content: t("user.confirmUnbindMfa"),
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
        async onOk() {
          setCanceling(true);
          try {
            const data: any = await Axios.delete("/api/v2/mfa/totp/associate");

            if (data.code !== 200) {
              message.error(data.message);
            } else {
              message.success(t("user.unbindSuccess"));
              fetchBindInfo();
              setCurrentStep(0);
            }
            setCanceling(false);
          } catch (e) {
            message.error(t("user.unbindFail"));
            setCanceling(false);
          }
        },
      })
    );
  };

  const fetchBindInfo = useCallback(async () => {
    setIsSpinning(true);
    try {
      const data: any = await Axios.post("/api/v2/mfa/totp/associate", {
        authenticator_type: "totp",
      });
      setSecret(data.data.recovery_code);
      setQrcode(data.data.qrcode_data_url);
      setIsSpinning(false);
    } catch (e) {
      message.error(t("user.bindInfoFetchFail"));
      setIsSpinning(false);
    }
  }, [t]);

  useEffect(() => {
    (async () => {
      setIsSpinning(true);
      try {
        const data: any = await Axios.get("/api/v2/mfa/authenticator?type=totp");
        if (data.data.some((item: any) => item.enable)) {
          setCurrentStep(3);
          setIsSpinning(false);
          return;
        }
        fetchBindInfo();
      } catch (e) {
        setIsSpinning(false);
      }
    })();
  }, [fetchBindInfo]);

  const STEP_MAP = {
    0: <ScanQrcode qrcode={qrcode} />,
    1: <InputSaftyCode {...{ saftyCode, setSaftyCode }} />,
    2: <SaveSecretKey {...{ secret, setIsSaved, isSaved }} />,
    3: <BindSuccess />,
  };

  const DISABLE_MAP = {
    0: () => false,
    1: () => saftyCode.some((item) => !item),
    2: () => !isSaved,
    3: () => false,
  };

  const [currentStep, setCurrentStep] = useState<keyof typeof STEP_MAP>(0);
  const handleNextStep = async () => {
    if (currentStep === 1) {
      await handleBind();
    } else {
      setCurrentStep((currentStep + 1) as keyof typeof STEP_MAP);
    }
  };
  const handlePrevStep = async () => {
    setCurrentStep((currentStep - 1) as keyof typeof STEP_MAP);
  };

  return (
    <Spin spinning={isSpinning}>
      <div className={styles.mfaWrapper}>
        <h3 className={styles.mfaTitle}>{t("user.mfaBind")}</h3>

        <div className={styles.mainContent}>
          <Steps size="small" current={currentStep}>
            <Step title={t("user.addMfa")} />
            <Step title={t("user.inputSafteyCode")} />
            <Step title={t("user.saveRecoverCode")} />
            <Step title={t("user.bindFinish")} />
          </Steps>

          <div className={styles.stepContent}>{STEP_MAP[currentStep]}</div>

          {currentStep === 1 && (
            <Button className={styles.btnItem} loading={btnLoading} size="large" onClick={handlePrevStep}>
              {t("user.prevStep")}
            </Button>
          )}
          {currentStep !== 3 && (
            <Button
              className={styles.btnItem}
              loading={btnLoading}
              disabled={DISABLE_MAP[currentStep]()}
              size="large"
              type="primary"
              onClick={handleNextStep}
            >
              {t("user.nextStep")}
            </Button>
          )}

          {currentStep === 3 && (
            <Button className={styles.btnItem} size={"large"} loading={canceling} onClick={handleUnbind}>
              {t("login.releaseBind")}
            </Button>
          )}
        </div>
      </div>
    </Spin>
  );
};
